import React, { useState } from 'react';
import Header from './Header';
import charactersData from '../story/the-missing-hour/characters_data.json';
import evidenceApi from '../services/evidenceApi';
import './CaseFile.css';

const CaseFile = () => {
  const [caseData, setCaseData] = useState({
    suspect: '',
    suspectReason: '',
    location: '',
    weapon: '',
    motive: '',
    additionalInfo: '',
    evidence: []
  });

  const [newEvidence, setNewEvidence] = useState({
    description: '',
    notes: ''
  });

  const [activeSection, setActiveSection] = useState('evidence'); // 'evidence' or 'prosecution'
  
  // Modal states
  const [showSuspectModal, setShowSuspectModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showWeaponModal, setShowWeaponModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [showAddEvidenceModal, setShowAddEvidenceModal] = useState(false);
  
  // Evidence submission state
  const [evidenceSubmission, setEvidenceSubmission] = useState({
    evidence_type: '',
    evidence_name: '',
    additional_notes: '',
    image_file: null
  });
  const [isSubmittingEvidence, setIsSubmittingEvidence] = useState(false);
  const [evidenceReviewResult, setEvidenceReviewResult] = useState(null);

  const handleInputChange = (field, value) => {
    setCaseData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEvidenceAdd = () => {
    if (newEvidence.description.trim()) {
      setCaseData(prev => ({
        ...prev,
        evidence: [...prev.evidence, { ...newEvidence, id: Date.now() }]
      }));
      setNewEvidence({ description: '', notes: '' });
    }
  };

  const handleEvidenceRemove = (evidenceId) => {
    setCaseData(prev => ({
      ...prev,
      evidence: prev.evidence.filter(e => e.id !== evidenceId)
    }));
  };

  // Evidence submission handlers
  const handleEvidenceInputChange = (field, value) => {
    setEvidenceSubmission(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setEvidenceSubmission(prev => ({
        ...prev,
        image_file: file
      }));
    }
  };

  const handleSubmitEvidence = async () => {
    try {
      setIsSubmittingEvidence(true);
      
      // Validate evidence data
      const validationErrors = evidenceApi.validateEvidenceData(evidenceSubmission);
      if (validationErrors.length > 0) {
        alert('Validation errors: ' + validationErrors.join(', '));
        return;
      }

      // Prepare evidence data for submission
      const evidenceData = {
        evidence_type: evidenceSubmission.evidence_type,
        evidence_name: evidenceSubmission.evidence_name,
        additional_notes: evidenceSubmission.additional_notes,
        story_context: evidenceApi.buildStoryContext()
      };

      // Add image data if present
      if (evidenceSubmission.image_file) {
        evidenceData.image_data = await evidenceApi.imageToBase64(evidenceSubmission.image_file);
      }

      // Submit to API
      const result = await evidenceApi.submitEvidence(evidenceData);
      setEvidenceReviewResult(result);
      
      // Reset form
      setEvidenceSubmission({
        evidence_type: '',
        evidence_name: '',
        additional_notes: '',
        image_file: null
      });
      
    } catch (error) {
      console.error('Evidence submission failed:', error);
      alert('Evidence submission failed: ' + error.message);
    } finally {
      setIsSubmittingEvidence(false);
    }
  };

  const resetEvidenceForm = () => {
    setEvidenceSubmission({
      evidence_type: '',
      evidence_name: '',
      additional_notes: '',
      image_file: null
    });
    setEvidenceReviewResult(null);
    setShowAddEvidenceModal(false);
  };

  const handleSaveDraft = () => {
    // TODO: Implement save draft functionality
    console.log('Saving draft:', caseData);
  };

  const handleSubmitCase = () => {
    // TODO: Implement case submission functionality
    console.log('Submitting case:', caseData);
  };

  // Get suspects from characters data (excluding released/ruled out suspects)
  const suspects = charactersData.characters.filter(char => 
    char.role === 'suspect' && char.status !== 'ruled_out'
  );
  
  // Helper function to get suspect image
  const getSuspectImageSource = (suspect) => {
    if (suspect.profile_photo && suspect.profile_photo !== null) {
      return suspect.profile_photo;
    }
    if (suspect.role === 'suspect') {
      return `/story/the-missing-hour/suspects/${suspect.id}_mugshot.jpg`;
    }
    return null;
  };
  
  // Handle suspect selection
  const handleSuspectSelect = (suspect) => {
    handleInputChange('suspect', suspect.name);
    setShowSuspectModal(false);
  };

  // Helper function to check if a field is completed
  const isFieldCompleted = (fieldName) => {
    if (fieldName === 'evidence') {
      return caseData.evidence.length > 0;
    }
    return caseData[fieldName] && caseData[fieldName].trim() !== '';
  };

  // Check if all required fields are completed (excluding additionalInfo)
  const areRequiredFieldsCompleted = () => {
    return isFieldCompleted('suspect') &&
           isFieldCompleted('suspectReason') &&
           isFieldCompleted('location') &&
           isFieldCompleted('weapon') &&
           isFieldCompleted('motive') &&
           isFieldCompleted('evidence');
  };
  
  // Mock evidence data with location and weapon tags
  const evidenceData = [
    { id: 'E1', description: 'Crime scene - Field adjoining farmhouse', type: 'location', tags: ['location'] },
    { id: 'E2', description: 'Blood-stained knife', type: 'weapon', tags: ['weapon', 'physical'] },
    { id: 'E3', description: 'Victim\'s mobile phone', type: 'digital', tags: ['digital'] },
    { id: 'E4', description: 'Soil samples from crime scene', type: 'trace', tags: ['location', 'trace'] },
    { id: 'E5', description: 'CCTV footage from industrial estate', type: 'audio_visual', tags: ['location', 'audio_visual'] }
  ];

  // Filter evidence by tags
  const locationEvidence = evidenceData.filter(ev => ev.tags.includes('location'));
  const weaponEvidence = evidenceData.filter(ev => ev.tags.includes('weapon'));

  return (
    <div>
      <Header />
      
      {/* Official Logo and Header */}
      <div className="case-file-official-header">
        <div className="official-logo">
          <img 
            src="/images/crown-court-logo.jpg" 
            alt="Crown Court Logo" 
            className="crown-court-logo"
          />
        </div>
        <div className="official-title">
          <h1>POLICE CASE FILE</h1>
          <h2>CRIMINAL INVESTIGATION DEPARTMENT</h2>
          <p>Case Reference: CF-2025-001</p>
          <p>Date: {new Date().toLocaleDateString('en-GB')}</p>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="case-file-sections">
        <button 
          className={`section-tab ${activeSection === 'evidence' ? 'active' : ''}`}
          onClick={() => setActiveSection('evidence')}
        >
          <img src="/images/icon-fingerprint.svg" alt="Fingerprint" width="24" height="24" style={{ marginBottom: '8px', filter: 'brightness(0) saturate(100%)' }} />
          Stage 1.<br />
          Evidence<br />
          Collection
        </button>
        <button 
          className={`section-tab ${activeSection === 'prosecution' ? 'active' : ''}`}
          onClick={() => setActiveSection('prosecution')}
        >
          <img src="/images/icon-gavel.svg" alt="Gavel" width="24" height="24" style={{ marginBottom: '8px', filter: 'brightness(0) saturate(100%)' }} />
          Stage 2.<br />
          Courtroom<br />
          Tactics
        </button>
        <button 
          className={`section-tab ${activeSection === 'stage3' ? 'active' : ''}`}
          onClick={() => setActiveSection('stage3')}
        >
          <img src="/images/icon-judge.svg" alt="Judge" width="24" height="24" style={{ marginBottom: '8px', filter: 'brightness(0) saturate(100%)' }} />
          Stage 3.<br />
          Final<br />
          Review
        </button>
      </div>

      {/* Evidence Collection Section */}
      {activeSection === 'evidence' && (
        <div className="case-file-content">
          <div className="case-file-section">
            <h3>PRIMARY SUSPECT IDENTIFICATION</h3>
            <div className="case-file-field">
              <label>SUSPECT NAME</label>
              <div className="field-input-container">
                <button 
                  className="case-file-select-button"
                  onClick={() => setShowSuspectModal(true)}
                >
                  {caseData.suspect || 'Click to select suspect...'}
                </button>
                {isFieldCompleted('suspect') && <span className="field-checkmark">✓</span>}
              </div>
            </div>
            <div className="case-file-field">
              <label>REASON FOR SUSPICION</label>
              <div className="field-input-container">
                <textarea
                  className="case-file-textarea"
                  value={caseData.suspectReason}
                  onChange={(e) => handleInputChange('suspectReason', e.target.value)}
                  placeholder="Detail the reasons this person is considered a suspect..."
                  rows="3"
                />
                {isFieldCompleted('suspectReason') && <span className="field-checkmark">✓</span>}
              </div>
            </div>
          </div>

          <div className="case-file-section">
            <h3>CRIME SCENE DETAILS</h3>
            <div className="case-file-field">
              <label>LOCATION OF INCIDENT</label>
              <div className="field-input-container">
                <button 
                  className="case-file-select-button"
                  onClick={() => setShowLocationModal(true)}
                >
                  {caseData.location || 'Click to select location from evidence...'}
                </button>
                {isFieldCompleted('location') && <span className="field-checkmark">✓</span>}
              </div>
            </div>
            <div className="case-file-field">
              <label>WEAPON USED</label>
              <div className="field-input-container">
                <button 
                  className="case-file-select-button"
                  onClick={() => setShowWeaponModal(true)}
                >
                  {caseData.weapon || 'Click to select weapon from evidence...'}
                </button>
                {isFieldCompleted('weapon') && <span className="field-checkmark">✓</span>}
              </div>
            </div>
          </div>

          <div className="case-file-section">
            <h3>MOTIVE ANALYSIS</h3>
            <div className="case-file-field">
              <label>PRIMARY MOTIVE</label>
              <div className="field-input-container">
                <select
                  className="case-file-select"
                  value={caseData.motive}
                  onChange={(e) => handleInputChange('motive', e.target.value)}
                >
                  <option value="">Select motive...</option>
                  <option value="revenge">Revenge</option>
                  <option value="jealousy">Jealousy</option>
                  <option value="financial">Financial Gain</option>
                  <option value="cover_up">Cover Up</option>
                  <option value="random">Random/Opportunistic</option>
                  <option value="other">Other</option>
                </select>
                {isFieldCompleted('motive') && <span className="field-checkmark">✓</span>}
              </div>
            </div>
          </div>

          <div className="case-file-section">
            <h3>EVIDENCE LINKING</h3>
            
            {/* Evidence Preparation Note */}
            <p className="add-evidence-note">
              Prepare and link evidence from the database to build your case. Select evidence items and provide supporting arguments to strengthen your investigation.
            </p>
            
            {/* Evidence Input Group - Show when adding new evidence or when no evidence exists */}
            {(caseData.evidence.length === 0 || newEvidence.description) && (
              <div className="evidence-input-group">
                <div className="case-file-field">
                  <label>SELECT EVIDENCE</label>
                  <button 
                    className="case-file-select-button"
                    onClick={() => setShowEvidenceModal(true)}
                  >
                    {newEvidence.description || 'Click to select evidence from database...'}
                  </button>
                </div>
                
                {/* Supporting Argument - Only show when evidence is selected */}
                {(() => {
                  console.log('Rendering check - newEvidence.description:', newEvidence.description);
                  console.log('Full newEvidence state:', newEvidence);
                  return newEvidence.description && (
                    <div className="case-file-field">
                      <label>SUPPORTING ARGUMENT</label>
                      <textarea
                        className="case-file-textarea"
                        value={newEvidence.notes}
                        onChange={(e) => setNewEvidence(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="How this evidence supports the case against the suspect..."
                        rows="3"
                      />
                    </div>
                  );
                })()}
                
                {/* Link Evidence Button - Only show when evidence is selected */}
                {newEvidence.description && (
                  <button 
                    className="add-evidence-btn"
                    onClick={handleEvidenceAdd}
                    disabled={!newEvidence.description}
                  >
                    LINK EVIDENCE
                  </button>
                )}
              </div>
            )}

            {/* Evidence List - Show when evidence is linked */}
            {caseData.evidence.length > 0 && (
              <div className="evidence-list">
                <h4>LINKED EVIDENCE ITEMS</h4>
                {caseData.evidence.map((evidence) => (
                  <div key={evidence.id} className="evidence-item">
                    <div className="evidence-content">
                      <strong>{evidence.description}</strong>
                      {evidence.notes && <p>{evidence.notes}</p>}
                    </div>
                    <button 
                      className="remove-evidence-btn"
                      onClick={() => handleEvidenceRemove(evidence.id)}
                    >
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add More Evidence Button - Show when evidence exists and not currently adding new evidence */}
            {caseData.evidence.length > 0 && !newEvidence.description && (
              <div className="add-more-evidence">
                <button 
                  className="add-evidence-btn"
                  onClick={() => {
                    setNewEvidence({ description: '', notes: '' });
                    setShowEvidenceModal(true);
                  }}
                >
                  ADD MORE EVIDENCE
                </button>
              </div>
            )}
          </div>

          <div className="case-file-section">
            <h3>ADDITIONAL INFORMATION</h3>
            <div className="case-file-field">
              <label>INVESTIGATIVE NOTES</label>
              <div className="field-input-container">
                <textarea
                  className="case-file-textarea"
                  value={caseData.additionalInfo}
                  onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                  placeholder="Any additional information, observations, or investigative notes..."
                  rows="4"
                />
                {isFieldCompleted('additionalInfo') && <span className="field-checkmark">✓</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Courtroom Tactics Section */}
      {activeSection === 'prosecution' && (
        <div className="case-file-content">
          <div className="case-file-section">
            <h3>COURTROOM PRESENTATION STRATEGY</h3>
            <div className="case-file-field">
              <label>OPENING STATEMENT APPROACH</label>
              <textarea
                className="case-file-textarea"
                placeholder="How to present the case to the jury in opening statements..."
                rows="3"
              />
            </div>
            <div className="case-file-field">
              <label>KEY WITNESSES TO CALL</label>
              <textarea
                className="case-file-textarea"
                placeholder="List of critical witnesses and their testimony order..."
                rows="3"
              />
            </div>
            <div className="case-file-field">
              <label>EVIDENCE PRESENTATION ORDER</label>
              <textarea
                className="case-file-textarea"
                placeholder="Strategic order for presenting evidence to build the case..."
                rows="3"
              />
            </div>
          </div>
          
          <div className="case-file-section">
            <h3>DEFENCE COUNTER-ARGUMENTS</h3>
            <div className="case-file-field">
              <label>ANTICIPATED DEFENCE STRATEGIES</label>
              <textarea
                className="case-file-textarea"
                placeholder="What arguments the defence might use and how to counter them..."
                rows="3"
              />
            </div>
            <div className="case-file-field">
              <label>WEAKNESSES TO ADDRESS</label>
              <textarea
                className="case-file-textarea"
                placeholder="Potential weaknesses in the case and how to strengthen them..."
                rows="3"
              />
            </div>
          </div>
          
          <div className="case-file-section">
            <h3>CLOSING ARGUMENT STRATEGY</h3>
            <div className="case-file-field">
              <label>KEY POINTS TO EMPHASISE</label>
              <textarea
                className="case-file-textarea"
                placeholder="Main arguments to emphasise in closing statements..."
                rows="3"
              />
            </div>
            <div className="case-file-field">
              <label>JURY INSTRUCTIONS FOCUS</label>
              <textarea
                className="case-file-textarea"
                placeholder="Specific jury instructions to request and emphasise..."
                rows="3"
              />
            </div>
          </div>
        </div>
      )}

      {/* Final Review Section */}
      {activeSection === 'stage3' && (
        <div className="case-file-content">
          <div className="case-file-section">
            <h3>FINAL CASE REVIEW</h3>
            <div className="case-file-field">
              <label>CASE STRENGTH ASSESSMENT</label>
              <textarea
                className="case-file-textarea"
                placeholder="Overall assessment of case strength and likelihood of conviction..."
                rows="3"
              />
            </div>
            <div className="case-file-field">
              <label>FINAL EVIDENCE CHECK</label>
              <textarea
                className="case-file-textarea"
                placeholder="Final review of all evidence and witness statements..."
                rows="3"
              />
            </div>
          </div>
          
          <div className="case-file-section">
            <h3>READINESS FOR TRIAL</h3>
            <div className="case-file-field">
              <label>TRIAL PREPARATION STATUS</label>
              <textarea
                className="case-file-textarea"
                placeholder="Current status of trial preparation and any remaining tasks..."
                rows="3"
              />
            </div>
            <div className="case-file-field">
              <label>FINAL NOTES</label>
              <textarea
                className="case-file-textarea"
                placeholder="Any final notes or considerations before submission..."
                rows="3"
              />
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="case-file-actions">
        <button className="save-draft-btn" onClick={handleSaveDraft}>
          SAVE DRAFT
        </button>
        <button 
          className="submit-case-btn" 
          onClick={handleSubmitCase}
          disabled={!areRequiredFieldsCompleted()}
          style={{ opacity: areRequiredFieldsCompleted() ? 1 : 0.5 }}
        >
          SUBMIT TO CPS
        </button>
      </div>

      {/* Suspect Selection Modal */}
      {showSuspectModal && (
        <div className="modal-overlay" onClick={() => {
          setShowSuspectModal(false);
        }}>
          <div className="suspect-modal-container" onClick={e => e.stopPropagation()}>
            
            {/* Suspect List View */}
            <div className="suspect-list-view">
              <div className="modal-header">
                <h3>SELECT SUSPECT</h3>
                <button className="modal-close" onClick={() => {
                  setShowSuspectModal(false);
                }}>×</button>
              </div>
              <div className="modal-body">
                {suspects.map((suspect) => (
                  <div
                    key={suspect.id}
                    className="suspect-modal-item"
                  >
                    <div className="suspect-mugshot-container">
                      {getSuspectImageSource(suspect) ? (
                        <img 
                          src={getSuspectImageSource(suspect)} 
                          alt={`${suspect.name} mugshot`}
                          className="suspect-mugshot"
                        />
                      ) : (
                        <div className="suspect-mugshot-placeholder">
                          {suspect.name.split(' ').map(n => n[0]).join('')}
                        </div>
                      )}
                    </div>
                    <div className="suspect-modal-info">
                      <div className="suspect-modal-name">{suspect.name}</div>
                      <div className="suspect-modal-connection">{suspect.connection_to_victim}</div>
                      <button 
                        className="select-suspect-btn"
                        onClick={() => handleSuspectSelect(suspect)}
                      >
                        SELECT SUSPECT
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>


          </div>
        </div>
      )}

      {/* Location Selection Modal */}
      {showLocationModal && (
        <div className="modal-overlay" onClick={() => setShowLocationModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>SELECT LOCATION FROM EVIDENCE</h3>
              <button className="modal-close" onClick={() => setShowLocationModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {locationEvidence.map((evidence) => (
                <div
                  key={evidence.id}
                  className="modal-item"
                  onClick={() => {
                    handleInputChange('location', evidence.description);
                    setShowLocationModal(false);
                  }}
                >
                  <div className="modal-item-main">
                    <div className="modal-item-title">{evidence.description}</div>
                    <div className="modal-item-type">Evidence ID: {evidence.id}</div>
                  </div>
                  <span className="modal-item-status">{evidence.type}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Weapon Selection Modal */}
      {showWeaponModal && (
        <div className="modal-overlay" onClick={() => setShowWeaponModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>SELECT WEAPON FROM EVIDENCE</h3>
              <button className="modal-close" onClick={() => setShowWeaponModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {weaponEvidence.map((evidence) => (
                <div
                  key={evidence.id}
                  className="modal-item"
                  onClick={() => {
                    handleInputChange('weapon', evidence.description);
                    setShowWeaponModal(false);
                  }}
                >
                  <div className="modal-item-main">
                    <div className="modal-item-title">{evidence.description}</div>
                    <div className="modal-item-type">Evidence ID: {evidence.id}</div>
                  </div>
                  <span className="modal-item-status">{evidence.type}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Evidence Selection Modal */}
      {showEvidenceModal && (
        <div className="modal-overlay" onClick={() => setShowEvidenceModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>SELECT EVIDENCE TO LINK</h3>
              <button className="modal-close" onClick={() => setShowEvidenceModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {evidenceData.map((evidence) => (
                <div
                  key={evidence.id}
                  className="modal-item"
                  onClick={() => {
                    console.log('Before selection - newEvidence:', newEvidence);
                    setNewEvidence(prev => {
                      const updated = { 
                        ...prev, 
                        description: evidence.description,
                        notes: prev.notes || '' // Preserve existing notes
                      };
                      console.log('After selection - updated:', updated);
                      return updated;
                    });
                    setShowEvidenceModal(false);
                  }}
                >
                  <div className="modal-item-main">
                    <div className="modal-item-title">{evidence.description}</div>
                    <div className="modal-item-type">Evidence ID: {evidence.id}</div>
                    <div className="modal-item-location">Type: {evidence.type}</div>
                  </div>
                  <span className="modal-item-status">{evidence.type}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Evidence Submission Modal */}
      {showAddEvidenceModal && (
        <div className="modal-overlay" onClick={resetEvidenceForm}>
          <div className="evidence-modal-container" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>SUBMIT NEW EVIDENCE</h3>
              <button className="modal-close" onClick={resetEvidenceForm}>×</button>
            </div>
            
            <div className="modal-body">
              {!evidenceReviewResult ? (
                // Evidence Submission Form
                <div className="evidence-submission-form">
                  <div className="form-field">
                    <label>EVIDENCE TYPE *</label>
                    <select
                      value={evidenceSubmission.evidence_type}
                      onChange={(e) => handleEvidenceInputChange('evidence_type', e.target.value)}
                      required
                    >
                      <option value="">Select evidence type...</option>
                      <option value="physical">Physical Evidence</option>
                      <option value="digital">Digital Evidence</option>
                      <option value="biological">Biological Evidence</option>
                      <option value="trace">Trace Evidence</option>
                      <option value="documentary">Documentary Evidence</option>
                      <option value="audio_visual">Audio/Visual Evidence</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label>EVIDENCE NAME *</label>
                    <input
                      type="text"
                      value={evidenceSubmission.evidence_name}
                      onChange={(e) => handleEvidenceInputChange('evidence_name', e.target.value)}
                      placeholder="e.g., Blood-stained knife, CCTV footage, Soil sample..."
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>ADDITIONAL NOTES</label>
                    <textarea
                      value={evidenceSubmission.additional_notes}
                      onChange={(e) => handleEvidenceInputChange('additional_notes', e.target.value)}
                      placeholder="Describe the evidence, where it was found, any relevant details..."
                      rows="3"
                    />
                  </div>

                  <div className="form-field">
                    <label>IMAGE UPLOAD (OPTIONAL)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="file-upload"
                    />
                    <p className="file-upload-note">
                      Upload an image for AI analysis. Max size: 5MB. Supported formats: JPG, PNG, GIF
                    </p>
                  </div>

                  <div className="form-actions">
                    <button 
                      className="control-button secondary"
                      onClick={resetEvidenceForm}
                      disabled={isSubmittingEvidence}
                    >
                      CANCEL
                    </button>
                    <button 
                      className="control-button primary"
                      onClick={handleSubmitEvidence}
                      disabled={isSubmittingEvidence || !evidenceSubmission.evidence_type || !evidenceSubmission.evidence_name}
                    >
                      {isSubmittingEvidence ? 'SUBMITTING...' : 'SUBMIT FOR REVIEW'}
                    </button>
                  </div>
                </div>
              ) : (
                // Evidence Review Results
                <div className="evidence-review-results">
                  <div className="review-header">
                    <h4>EVIDENCE REVIEW COMPLETE</h4>
                    <div className="relevance-score">
                      Relevance Score: <span className="score-value">{evidenceReviewResult.relevance_score}/10</span>
                    </div>
                  </div>

                  <div className="review-section">
                    <h5>EVIDENCE ASSESSMENT</h5>
                    <p>{evidenceReviewResult.evidence_review}</p>
                  </div>

                  <div className="review-section">
                    <h5>STORY ALIGNMENT</h5>
                    <p>{evidenceReviewResult.story_alignment}</p>
                  </div>

                  <div className="review-section">
                    <h5>INVESTIGATIVE VALUE</h5>
                    <p>{evidenceReviewResult.investigative_value}</p>
                  </div>

                  {evidenceReviewResult.image_analysis && (
                    <div className="review-section">
                      <h5>IMAGE ANALYSIS</h5>
                      <p>{evidenceReviewResult.image_analysis}</p>
                    </div>
                  )}

                  <div className="review-section">
                    <h5>RECOMMENDED ACTIONS</h5>
                    <p>{evidenceReviewResult.recommended_actions}</p>
                  </div>

                  <div className="form-actions">
                    <button 
                      className="control-button primary"
                      onClick={resetEvidenceForm}
                    >
                      SUBMIT ANOTHER EVIDENCE
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseFile;
